import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { fetchAllFilteredRows } from "@/components/shared/data-table/data-table-export";
import { financeDashboardKeys } from "@/features/finance/hooks/query-keys";
import { accountKeys } from "@/features/accounts/hooks/query-keys";
import { purchaseOrderKeys } from "@/features/purchase-orders/hooks/query-keys";
import { dashboardKeys } from "@/features/dashboard/hooks/query-keys";
import { paymentKeys } from "@/features/vendor-payments/hooks/query-keys";
import { vendorCreditKeys } from "@/features/vendor-credits/hooks/query-keys";
import {
  reconcileProductWrite,
  reconcilePurchaseOrderHeaderWrite,
} from "@/features/finance/hooks/reconcile-product-write";
import {
  addPurchaseOrderProducts,
  createPurchaseOrder,
  createVendorPayment,
  deletePurchaseOrder,
  getPurchaseOrder,
  getPurchaseOrders,
  getPurchaseOrderTotals,
  removePurchaseOrderProduct,
  transferPurchaseOrderProduct,
  updatePurchaseOrder,
  updatePurchaseOrderProduct,
} from "@/features/purchase-orders/hooks/purchase-orders-client";
import type {
  CreatePurchaseOrderPayload,
  CreateVendorPaymentPayload,
  PurchaseOrder,
  PurchaseOrderFilters,
  PurchaseOrderProductLine,
  TransferPaymentHandling,
  UpdatePurchaseOrderProductPayload,
  VendorPayment,
} from "@/features/purchase-orders/types";
import { poKanbanKeys } from "@/features/purchase-orders/hooks/query-keys";
import {
  deleteVendorPayment,
  getVendorPayments,
} from "@/features/vendor-payments/hooks/vendor-payments-client";
import { triggerAutoPdf } from "@/hooks/pdf/trigger-auto-pdf";
import { getAccounts } from "@/lib/db/accounts";

export function usePurchaseOrders(filters: PurchaseOrderFilters, options?: { enabled?: boolean }) {
  const queryFilters = {
    search: filters.search,
    account_id: filters.account_id,
    status: filters.status,
    status_in: filters.status_in,
    date_from: filters.date_from,
    date_to: filters.date_to,
    limit: filters.limit,
    merge_date: filters.merge_date,
    merge_target_order: filters.merge_target_order,
    offset: filters.offset,
    sort_direction: filters.sort_direction,
    sort_field: filters.sort_field,
  };

  return useQuery({
    enabled: options?.enabled ?? true,
    queryKey: purchaseOrderKeys.list(queryFilters),
    queryFn: () => getPurchaseOrders(filters),
  });
}

/**
 * Totals for the whole filtered set.
 *
 * Keyed on the filters that change the set, so paging or re-sorting reuses a
 * figure that cannot have moved.
 */
export function usePurchaseOrderTotals(filters: PurchaseOrderFilters) {
  const totalsFilters: PurchaseOrderFilters = {
    account_id: filters.account_id,
    date_from: filters.date_from,
    date_to: filters.date_to,
    search: filters.search,
    status: filters.status,
    status_in: filters.status_in,
  };

  return useQuery({
    queryKey: purchaseOrderKeys.totals(totalsFilters as unknown as Record<string, unknown>),
    queryFn: () => getPurchaseOrderTotals(totalsFilters),
  });
}

/** Every purchase order the current filters select, for CSV export. */
export function fetchPurchaseOrdersForExport(
  filters: PurchaseOrderFilters,
): Promise<PurchaseOrder[]> {
  return fetchAllFilteredRows<PurchaseOrder>(({ limit, offset }) =>
    getPurchaseOrders({ ...filters, limit, offset }),
  );
}

export function usePurchaseOrderVendors(limit = 200) {
  const vendorLimit = limit;
  return useQuery({
    queryKey: accountKeys.list({ type: "vendor", limit: vendorLimit }),
    queryFn: async () =>
      await getAccounts({
        type: "vendor",
        limit: vendorLimit,
      }),
  });
}

interface UsePurchaseOrderVendorsOptions {
  enabled?: boolean;
  limit?: number;
  search?: string;
}

export function usePurchaseOrderVendorsSearch(options?: UsePurchaseOrderVendorsOptions) {
  const limit = options?.limit ?? 200;

  return useQuery({
    enabled: options?.enabled ?? true,
    queryKey: accountKeys.list({
      type: "vendor",
      limit,
      search: options?.search || "",
    }),
    queryFn: async () =>
      await getAccounts({
        type: "vendor",
        limit,
        search: options?.search || undefined,
      }),
  });
}

export function usePurchaseOrderDetail(id: string | undefined) {
  return useQuery({
    enabled: Boolean(id),
    queryKey: purchaseOrderKeys.detail(id || "missing"),
    queryFn: () => getPurchaseOrder(id || ""),
    staleTime: 5 * 60 * 1000,
  });
}

export function usePrefetchPurchaseOrderDetail() {
  const queryClient = useQueryClient();
  return useCallback(
    (purchaseOrderId: string) =>
      queryClient.prefetchQuery({
        queryFn: () => getPurchaseOrder(purchaseOrderId),
        queryKey: purchaseOrderKeys.detail(purchaseOrderId),
        staleTime: 5 * 60 * 1000,
      }),
    [queryClient],
  );
}

interface UpdatePurchaseOrderHeaderPayload {
  accountId: string;
  date?: string;
  id: string;
  notes?: string | null;
}

export function useUpdatePurchaseOrderHeader() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, accountId, date, notes }: UpdatePurchaseOrderHeaderPayload) =>
      await updatePurchaseOrder(id, {
        account_id: accountId,
        date,
        notes,
      }),
    onSuccess: async (_result, { id }) => {
      await reconcilePurchaseOrderHeaderWrite(queryClient, id);
    },
  });
}

export function useRecentVendorPayments(limit = 30, options?: { enabled?: boolean }) {
  return useQuery({
    enabled: options?.enabled ?? true,
    queryKey: paymentKeys.vendor.list({ limit, recent: true }),
    queryFn: async (): Promise<VendorPayment[]> => await getVendorPayments({ limit }),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
}

export function usePurchaseOrderTransferTargets(params: {
  sourcePurchaseOrderId: string;
  search?: string;
  enabled?: boolean;
}) {
  const { sourcePurchaseOrderId, search, enabled = true } = params;

  return useQuery({
    enabled,
    queryKey: purchaseOrderKeys.list({
      excludePurchaseOrderId: sourcePurchaseOrderId,
      search,
      transferTargets: true,
    }),
    queryFn: async () =>
      (
        await getPurchaseOrders({
          limit: 50,
          search,
        })
      ).filter((po) => po.id !== sourcePurchaseOrderId),
  });
}

export function useDeletePurchaseOrderMutation() {
  return useMutation({
    mutationFn: async (purchaseOrderId: string) => await deletePurchaseOrder(purchaseOrderId),
  });
}

/**
 * Records a vendor payment against a purchase order.
 *
 * A payment moves `total_paid` and `balance` on the server, so the purchase
 * order subtree, the vendor payment lists, and the kanban board's own
 * purchase-order projection (a disjoint `poKanbanKeys` cache root) are all
 * stale the moment the write lands. Reconciling here rather than at each call
 * site is what keeps a caller that only tracks local component state — the
 * kanban payment resolution dialog — from leaving a paid purchase order
 * rendered as unpaid and inviting a duplicate payment.
 *
 * The same balance move feeds both dashboards: the main dashboard sums
 * `gl_purchase_orders` payables and the finance dashboard buckets that balance
 * into payables aging while counting the payment as cash out. Their
 * ["dashboard"] / ["finance-dashboard"] roots are disjoint, so both are
 * invalidated explicitly.
 */
export function useCreateVendorPaymentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateVendorPaymentPayload) => await createVendorPayment(payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all }),
        queryClient.invalidateQueries({ queryKey: paymentKeys.vendor.all }),
        queryClient.invalidateQueries({ queryKey: poKanbanKeys.purchaseOrders.all() }),
        queryClient.invalidateQueries({ queryKey: dashboardKeys.all }),
        queryClient.invalidateQueries({ queryKey: financeDashboardKeys.all }),
      ]);
    },
  });
}

export function useCreatePurchaseOrderMutation() {
  return useMutation({
    mutationFn: async (payload: CreatePurchaseOrderPayload) => await createPurchaseOrder(payload),
    onSuccess: (result) => {
      if (!result.merged_into_existing) {
        triggerAutoPdf("purchase_order", result.purchase_order.id);
      }
    },
  });
}

/**
 * Removes a vendor payment from a purchase order.
 *
 * The mirror of {@link useCreateVendorPaymentMutation}: the deleted amount
 * comes back off `total_paid`, so the same subtrees have to be refetched
 * or the purchase order keeps reporting money it no longer holds. The RPC
 * (`xan_delete_vendor_payment`) also hard-deletes this payment's unapplied
 * remainder `gl_vendor_credits` row in the same transaction, so vendor-credit
 * balance, list, detail, and application views are stale as well. The restored
 * balance and the reversed cash-out likewise move both dashboards.
 */
export function useDeleteVendorPaymentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paymentId: string) => await deleteVendorPayment(paymentId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: purchaseOrderKeys.all }),
        queryClient.invalidateQueries({ queryKey: paymentKeys.vendor.all }),
        queryClient.invalidateQueries({ queryKey: vendorCreditKeys.all }),
        queryClient.invalidateQueries({ queryKey: poKanbanKeys.purchaseOrders.all() }),
        queryClient.invalidateQueries({ queryKey: dashboardKeys.all }),
        queryClient.invalidateQueries({ queryKey: financeDashboardKeys.all }),
      ]);
    },
  });
}

interface TransferProductPayload {
  paymentHandling: TransferPaymentHandling;
  productId: string;
  productName: string;
  sourcePurchaseOrderId: string;
  targetPurchaseOrderId: string;
}

export function useTransferPurchaseOrderProductMutation() {
  return useMutation({
    mutationFn: async ({
      sourcePurchaseOrderId,
      productId,
      targetPurchaseOrderId,
      paymentHandling,
      productName,
    }: TransferProductPayload) => {
      await transferPurchaseOrderProduct({
        payment_handling: paymentHandling,
        product_id: productId,
        source_purchase_order_id: sourcePurchaseOrderId,
        target_purchase_order_id: targetPurchaseOrderId,
      });
      return { productName };
    },
  });
}

/**
 * The shared shape of a purchase-order line write: the write itself, then the
 * cache reconciliation every one of them owes. Reconciling in `onSuccess` is
 * what leaves a refused write's caches exactly as the operator left them.
 */
function useProductWriteMutation<TVariables extends { purchaseOrderId: string }, TData>(
  mutationFn: (variables: TVariables) => Promise<TData>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: async (_result: TData, { purchaseOrderId }: TVariables) => {
      await reconcileProductWrite(queryClient, purchaseOrderId);
    },
  });
}

interface AddPurchaseOrderProductsPayload {
  products: PurchaseOrderProductLine[];
  purchaseOrderId: string;
}

export function useAddPurchaseOrderProductsMutation() {
  return useProductWriteMutation(
    async ({ products, purchaseOrderId }: AddPurchaseOrderProductsPayload) =>
      await addPurchaseOrderProducts(purchaseOrderId, products),
  );
}

interface UpdatePurchaseOrderProductVariables {
  payload: UpdatePurchaseOrderProductPayload;
  productId: string;
  purchaseOrderId: string;
}

export function useUpdatePurchaseOrderProductMutation() {
  return useProductWriteMutation(
    async ({ payload, productId }: UpdatePurchaseOrderProductVariables) =>
      await updatePurchaseOrderProduct(productId, payload),
  );
}

interface RemovePurchaseOrderProductVariables {
  productId: string;
  productName: string;
  purchaseOrderId: string;
}

export function useRemovePurchaseOrderProductMutation() {
  return useProductWriteMutation(
    async ({ productId, productName, purchaseOrderId }: RemovePurchaseOrderProductVariables) => {
      await removePurchaseOrderProduct(purchaseOrderId, productId);
      return { productName };
    },
  );
}
